import { action, computed, observable, runInAction } from 'mobx'
import {
  type CallCenterSearchResult,
  dedupeCallCenterMatches,
} from './call-center-search-result'
import { filterCallCenterMemberships } from './call-center-util'
import type {
  ListUserWorkspaceMembershipsFn,
  FetchWorkspaceFn,
} from '@/features/iam'
import type { Membership } from '@/features/iam/workspaces/Membership'
import { type Member } from '@/features/iam/members/Member'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import type { Id } from '@/infra/id'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import {
  type CallCenterMembershipItemActions,
  CallCenterMembershipItemViewModel,
} from './CallCenterMembershipItemViewModel'

export interface CallCenterMembershipItem {
  key: string
  workspaceId: Id
  workspace: Workspace | null
  member: Member | null
  type: 'Membership' | 'Waitlist'
  isWaitlistWorkspace: boolean
  loading: boolean
  error: string | null
}

/**
 * The fetch of the workspace whose waitlist the contact signed up for.
 */
interface WaitlistWorkspaceFetch {
  workspaceId: Id
  workspace: Workspace | null
  error: string | null
}

export class CallCenterSelectionViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  @observable.ref
  private accessor _waitlistWorkspaceFetch: WaitlistWorkspaceFetch | null = null

  @observable
  accessor selectedResult: CallCenterSearchResult | null = null

  @observable.ref
  accessor selectedMemberships: Membership[] = []

  constructor(
    private readonly _listUserWorkspaceMemberships: ListUserWorkspaceMembershipsFn,
    private readonly _fetchWorkspace: FetchWorkspaceFn,
    private readonly _membershipItemActions: CallCenterMembershipItemActions,
  ) {}

  @computed
  get hasSelection() {
    return this.selectedResult !== null
  }

  @computed
  private get _hasWaitlistSignup() {
    return this.selectedResult?.hubspotContact?.signedUpForWaitlist ?? false
  }

  @computed
  private get _waitlistWorkspaceId() {
    return this.selectedResult?.hubspotContact?.waitlistWorkspaceId ?? null
  }

  @computed
  private get _membershipItems(): CallCenterMembershipItem[] {
    const waitlistWorkspaceId = this._waitlistWorkspaceId
    const hasWaitlistSignup = this._hasWaitlistSignup

    const items: CallCenterMembershipItem[] = this.selectedMemberships.map(
      (membership) => {
        const workspaceId = membership.workspace.id
        const userPublicId = membership.member.user.publicId

        return {
          key: `membership:${workspaceId}:${userPublicId}`,
          workspaceId,
          workspace: membership.workspace,
          member: membership.member,
          type: 'Membership' as const,
          isWaitlistWorkspace:
            hasWaitlistSignup && workspaceId === waitlistWorkspaceId,
          loading: false,
          error: null,
        }
      },
    )

    if (hasWaitlistSignup && waitlistWorkspaceId) {
      const alreadyListed = items.some(
        (item) => item.workspaceId === waitlistWorkspaceId,
      )

      if (!alreadyListed) {
        const fetched =
          this._waitlistWorkspaceFetch?.workspaceId === waitlistWorkspaceId
            ? this._waitlistWorkspaceFetch
            : null
        items.push({
          key: `waitlist:${waitlistWorkspaceId}`,
          workspaceId: waitlistWorkspaceId,
          workspace: fetched?.workspace ?? null,
          member: null,
          type: 'Waitlist',
          isWaitlistWorkspace: true,
          loading: !fetched?.workspace && !fetched?.error,
          error: fetched?.error ?? null,
        })
      }
    }

    return items
  }

  /**
   * The workspaces the client is a member of.
   */
  @computed
  get membershipEntryVms() {
    return this._createItemVms('Membership')
  }

  /**
   * The workspace whose waitlist the HubSpot contact signed up for, when the
   * client is not a member of it yet.
   */
  @computed
  get waitlistEntryVms() {
    return this.selectedResult?.hubspotContact
      ? this._createItemVms('Waitlist')
      : []
  }

  @computed
  get shouldShowEmptyMembership() {
    return this._membershipItems.length === 0
  }

  /**
   * The member to describe the client with when there is no HubSpot contact.
   */
  @computed
  get primaryMember() {
    return this.selectedResult?.workspaceMembers[0]?.member ?? null
  }

  @computed
  get fullName() {
    const contact = this.selectedResult?.hubspotContact
    return contact
      ? [contact.firstName, contact.lastName].filter(Boolean).join(' ')
      : (this.primaryMember?.displayName ?? '')
  }

  @computed
  get email() {
    return (
      this.selectedResult?.hubspotContact?.email ||
      this.primaryMember?.email ||
      ''
    )
  }

  @computed
  get phone() {
    return (
      this.selectedResult?.hubspotContact?.phone ||
      this.primaryMember?.phone ||
      ''
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  @action.bound
  setSelectedResult(
    result: CallCenterSearchResult,
    initialMemberships: Membership[],
  ) {
    this.selectedResult = result
    this.selectedMemberships = initialMemberships
    this._updateWaitlistWorkspaceState()
  }

  @action.bound
  clear() {
    this.selectedResult = null
    this.selectedMemberships = []
  }

  /**
   * Fetches the workspaces the given users are members of.
   */
  async fetchMembershipDetails(userPublicIds: Id[], signal?: AbortSignal) {
    const membershipArrays = await Promise.all(
      userPublicIds.map((id) =>
        this._listUserWorkspaceMemberships(id, undefined, signal),
      ),
    )
    signal?.throwIfAborted()

    const dedupedMemberships = dedupeCallCenterMatches(
      filterCallCenterMemberships(membershipArrays.flat()),
    )

    runInAction(() => {
      this.selectedMemberships = dedupedMemberships
      this._updateWaitlistWorkspaceState()
    })
  }

  @action.bound
  private _updateWaitlistWorkspaceState() {
    const hasWaitlistSignup = this._hasWaitlistSignup
    const waitlistWorkspaceId = this._waitlistWorkspaceId

    if (!hasWaitlistSignup || !waitlistWorkspaceId) {
      return
    }

    const membership = this.selectedMemberships.find(
      (item) => item.workspace.id === waitlistWorkspaceId,
    )

    if (membership) {
      return
    }

    const fetched = this._waitlistWorkspaceFetch
    if (fetched?.workspaceId === waitlistWorkspaceId && !fetched.error) {
      return
    }

    void this._fetchWaitlistWorkspace(waitlistWorkspaceId)
  }

  /**
   * Fetches the waitlist workspace. A failure shows on the waitlist entry.
   * The result is dropped when another workspace was requested meanwhile.
   */
  private async _fetchWaitlistWorkspace(workspaceId: Id) {
    const setFetch = action((fetch: WaitlistWorkspaceFetch) => {
      if (this._waitlistWorkspaceFetch?.workspaceId === workspaceId) {
        this._waitlistWorkspaceFetch = fetch
      }
    })

    runInAction(() => {
      this._waitlistWorkspaceFetch = {
        workspaceId,
        workspace: null,
        error: null,
      }
    })
    try {
      const workspace = await this._fetchWorkspace(
        workspaceId,
        undefined,
        this._lifecycle.signal,
      )
      if (!workspace) {
        throw new Error('Unable to load workspace details.')
      }

      setFetch({ workspaceId, workspace, error: null })
    } catch (err) {
      setFetch({ workspaceId, workspace: null, error: (err as Error).message })
    }
  }

  private _createItemVms(type: CallCenterMembershipItem['type']) {
    const canInvite = () => !!this.selectedResult?.hubspotContact?.email
    return this._membershipItems
      .filter((item) => item.type === type)
      .map(
        (item) =>
          new CallCenterMembershipItemViewModel(
            item,
            canInvite,
            this._membershipItemActions,
          ),
      )
  }
}
